import React, { useCallback, useEffect, useMemo, useState } from "react";

import { InfoTooltip, SideDrawer } from "./BuilderUtils";
import DecisionEditor from "./DecisionEditor";
import { normalize } from "./layerSearch";
import {
  buildDecisionIndex, decisionFacetValues, facetsOf, searchDecisions,
} from "./decisionSearch";
import { bandsOf, fetchPoolPreview, poolEntriesOf, selectionOf, slotsDemanded } from "./measurePool";

/**
 * LE RÉSERVOIR DE MESURES DE L'ATELIER.
 *
 * Les pages qui tirent au sort (cf. l'onglet Contenu du composeur) puisent
 * toutes ici. Le réservoir est porté par l'ATELIER et non par les décisions :
 * une décision est un document partagé entre ateliers, « fait partie du
 * réservoir » n'a de sens que dans l'un d'eux.
 *
 * CE QUE CET ÉCRAN DOIT DIRE D'UN COUP D'ŒIL — et qui n'est visible nulle part
 * ailleurs : le réservoir tiendra-t-il jusqu'au dernier tour ? Un atelier de
 * trois mosaïques de huit cases demande vingt-quatre mesures dans le pire des
 * cas (tout ce qui est proposé est pris). En dessous, les derniers tours
 * s'afficheront incomplets, et la mosaïque change même de disposition sous huit
 * cartes. C'est le bandeau du haut, avant la liste.
 *
 * TROIS CHOIX D'AFFICHAGE, PARCE QU'UN RÉSERVOIR COMPTE TRENTE MESURES :
 *
 * 1. LE TITRE, PAS L'IDENTIFIANT. `interdire_arrosage_golfs` se déchiffre ;
 *    « Interdire l'arrosage des golfs » se lit. L'identifiant reste dessous,
 *    en petit : c'est lui qui figure dans les conséquences et les épinglages,
 *    on doit pouvoir le recopier.
 *
 * 2. LA LISTE SE REPLIE, et se replie d'elle-même quand elle est longue. Ce
 *    qu'on vient chercher ici neuf fois sur dix, c'est le bandeau de
 *    dimensionnement ; la liste des trente mesures repoussait le reste de
 *    l'atelier (scores, pile) hors de l'écran. Une mesure INTROUVABLE en base
 *    reste affichée même replié : ce qui demande une action ne se cache pas.
 *
 * 3. LES COÛTS SONT EN OPTION, ET MASQUÉS PAR DÉFAUT. Le coût, sa bande et le
 *    poids ne servent qu'à un usage précis — les planchers « au moins 2 peu
 *    coûteuses » des pages qui piochent. Une colonne de chiffres qu'on ne sait
 *    pas lire fait douter du reste de l'écran. L'option est donc décochée à
 *    chaque ouverture, sans exception : c'est un réglage qu'on va chercher
 *    quand on en a besoin, pas un état de l'atelier à surveiller.
 *
 * LE COÛT ET LA BANDE NE SONT PAS CALCULÉS ICI. Ils viennent de `/pool_preview`,
 * qui les tire de la même fonction que le jeu : le tableau ne peut donc pas
 * afficher un classement que le tirage ne suivrait pas.
 */

// Au-delà, la liste s'ouvre repliée. Douze : de quoi voir en entier le
// réservoir d'un atelier qui débute, pas celui d'un atelier écrit.
const FOLD_ABOVE = 12;

export default function MeasurePoolSection({ config, onSet, pagesById, token }) {
  const [allDecisions, setAllDecisions] = useState([]);
  const [classification, setClassification] = useState({});
  const [showPicker, setShowPicker] = useState(false);
  // Mesure ouverte dans le tiroir d'édition ("new" pour une création), ou null.
  const [drawerDecisionId, setDrawerDecisionId] = useState(null);
  // Filtre de la liste du réservoir (distinct de la recherche du sélecteur :
  // l'un cherche parmi trente, l'autre parmi deux cents).
  const [listQuery, setListQuery] = useState("");
  // Replié/déplié : `null` = on suit le défaut (replié si la liste est longue),
  // sinon c'est le choix de l'auteur, qui ne doit plus bouger sous lui.
  const [listOpenChoice, setListOpenChoice] = useState(null);
  // Les coûts sont TOUJOURS masqués à l'ouverture, y compris quand une page
  // déclare un plancher : c'est un réglage qu'on va voir quand on en a besoin,
  // pas un état de l'atelier à surveiller.
  const [showCosts, setShowCosts] = useState(false);
  // Incrémenté quand une mesure a été retouchée : son impact sur le budget
  // vient peut-être de changer, donc son coût et sa bande aussi. Le réservoir,
  // lui, n'a pas bougé — sans ce compteur le classement resterait celui d'avant.
  const [catalogVersion, setCatalogVersion] = useState(0);

  const entries = poolEntriesOf(config);
  const bands = bandsOf(config);

  const fetchDecisions = useCallback(() => (
    fetch(`${process.env.REACT_APP_API_URL}/get_all_decisions`, {
      headers: { Authorization: "Bearer " + token },
    })
      .then((r) => r.json())
      .then((list) => (Array.isArray(list) ? list : []))
      .catch(() => [])
  ), [token]);

  useEffect(() => { fetchDecisions().then(setAllDecisions); }, [fetchDecisions]);

  const decisionsById = useMemo(
    () => Object.fromEntries(allDecisions.map((d) => [d._id, d])),
    [allDecisions]
  );

  /* ── Ce que l'atelier demande au réservoir ───────────────────────── */

  const poolPages = (config.pile || [])
    .map((pageId) => ({ pageId, selection: selectionOf(pagesById[pageId]) }))
    .filter((row) => row.selection);
  const slots = slotsDemanded(config, pagesById);
  const unknownCount = entries.filter((e) => !decisionsById[e.decision_id]).length;

  const listOpen = listOpenChoice ?? entries.length <= FOLD_ABOVE;

  // Le classement dépend du réservoir, du score de décision et des bandes :
  // il se refait quand l'un des trois bouge, et pas à chaque frappe ailleurs
  // dans l'atelier. Coûts masqués, il ne se fait pas du tout — l'aller-retour
  // ne servirait à afficher rien.
  const poolSignature = JSON.stringify([
    entries.map((e) => e.decision_id), config.decision_metric_id, bands,
  ]);
  useEffect(() => {
    if (!showCosts || entries.length === 0) return setClassification({});
    let cancelled = false;
    fetchPoolPreview(token, { config, page: {}, samples: 0 })
      .then((data) => { if (!cancelled) setClassification(data.classification || {}); })
      .catch(() => { if (!cancelled) setClassification({}); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [poolSignature, token, showCosts, catalogVersion]);

  /* ── Modifier le réservoir ───────────────────────────────────────── */

  const patchPool = (partial) => {
    onSet("measure_pool", { bands, entries, ...config.measure_pool, ...partial });
  };
  const setEntries = (next) => patchPool({ entries: next });

  const addDecision = (decisionId) => {
    if (entries.some((e) => e.decision_id === decisionId)) return;
    setEntries([...entries, { decision_id: decisionId }]);
  };
  const removeEntry = (index) => setEntries(entries.filter((unused, i) => i !== index));
  // Le sélecteur ajoute ET retire : on y compose le réservoir par lots, et
  // corriger un clic de trop ne doit pas obliger à le refermer.
  const toggleDecision = (decisionId) => {
    const index = entries.findIndex((e) => e.decision_id === decisionId);
    if (index === -1) addDecision(decisionId);
    else removeEntry(index);
  };
  const setWeight = (index, value) => {
    const weight = value === "" ? undefined : Number(value);
    setEntries(entries.map((entry, i) => {
      if (i !== index) return entry;
      const next = { ...entry, weight };
      if (next.weight === undefined || Number.isNaN(next.weight)) delete next.weight;
      return next;
    }));
  };

  /* ── Éditer une mesure sans quitter l'atelier ────────────────────── */

  const openDecisionEditor = (decisionId) => setDrawerDecisionId(decisionId || "new");

  const closeDecisionDrawer = async () => {
    const wasNew = drawerDecisionId === "new";
    setDrawerDecisionId(null);
    const list = await fetchDecisions();
    if (wasNew) {
      // DecisionEditor embarqué ne renvoie pas l'id créé : on le déduit en
      // comparant le catalogue avant/après. Une mesure créée d'ici est créée
      // POUR ce réservoir, elle y entre donc sans second geste.
      const previous = new Set(allDecisions.map((d) => d._id));
      const created = list.find((d) => !previous.has(d._id));
      if (created) addDecision(created._id);
    }
    setAllDecisions(list);
    setCatalogVersion((v) => v + 1);
  };

  /* ── Les bandes de coût ──────────────────────────────────────────── */

  const costs = entries
    .map((e) => classification[e.decision_id]?.cost)
    .filter((c) => c != null)
    .sort((a, b) => a - b);
  const perBand = {};
  entries.forEach((entry) => {
    const band = classification[entry.decision_id]?.band;
    if (band) perBand[band] = (perBand[band] || 0) + 1;
  });

  const metricId = config.decision_metric_id || "";
  const metricName = config.score_dic?.[metricId]?.name || metricId || "budget";

  // Les seuils proposés découpent le réservoir en tranches de même effectif.
  // PROPOSÉS, PAS APPLIQUÉS D'OFFICE : des bandes recalculées à chaque ajout de
  // mesure redéfiniraient en douce les planchers de toutes les pages. Et parce
  // que c'est exactement ce que ce bouton risque de faire à des seuils déjà
  // écrits, il le demande avant.
  const suggestBands = () => {
    if (costs.length < bands.length) return;
    const already = bands.some((band, i) => i < bands.length - 1 && band.max_cost != null);
    if (already && !window.confirm(
      "Remplacer les seuils actuels ?\n\n"
      + "Les pages qui piochent expriment leurs planchers dans ces groupes "
      + "(« au moins 2 peu coûteuses ») : changer les seuils change ce que ces "
      + "planchers désignent."
    )) return;

    let previous = -Infinity;
    patchPool({
      bands: bands.map((band, index) => {
        // La dernière bande reste ouverte : une mesure plus chère que le
        // dernier seuil doit tomber quelque part.
        if (index === bands.length - 1) return { ...band, max_cost: null };
        const cut = costs[Math.floor(((index + 1) * costs.length) / bands.length)];
        previous = Math.max(previous, cut);
        return { ...band, max_cost: previous };
      }),
    });
  };

  const setBandMax = (index, value) => {
    patchPool({
      bands: bands.map((band, i) => (
        i === index ? { ...band, max_cost: value === "" ? null : Number(value) } : band
      )),
    });
  };

  /* ── Les lignes affichées ────────────────────────────────────────── */

  const poolDecisions = useMemo(
    () => entries.map((e) => decisionsById[e.decision_id]).filter(Boolean),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [poolSignature, decisionsById]
  );
  const poolIndex = useMemo(() => buildDecisionIndex(poolDecisions), [poolDecisions]);

  const trimmedQuery = listQuery.trim();
  const matchedIds = useMemo(() => {
    if (!trimmedQuery) return null;
    return new Set(searchDecisions(poolIndex, { query: trimmedQuery }).map((d) => d._id));
  }, [poolIndex, trimmedQuery]);

  // Ce qui DISTINGUE une mesure des autres du réservoir. Une étiquette portée
  // par les trente mesures (« acg ») n'informe personne : elle occupe la ligne
  // sans permettre de choisir. Sous trois mesures, tout est commun par
  // accident, donc rien n'est masqué.
  const discriminating = useMemo(() => {
    const counts = new Map();
    poolDecisions.forEach((decision) => {
      decisionFacetValues(decision).forEach(({ value }) => {
        counts.set(value, (counts.get(value) || 0) + 1);
      });
    });
    return ({ value }) => (
      poolDecisions.length < 3 || counts.get(value) !== poolDecisions.length
    );
  }, [poolDecisions]);

  const rowsFor = (entry, index) => ({ entry, index, decision: decisionsById[entry.decision_id] });
  const allRows = entries.map(rowsFor);
  const missingRows = allRows.filter((row) => !row.decision);
  const visibleRows = (listOpen ? allRows : missingRows)
    .filter((row) => {
      if (!matchedIds) return true;
      return matchedIds.has(row.entry.decision_id)
        || normalize(row.entry.decision_id).includes(normalize(trimmedQuery));
    })
    .sort((a, b) => (
      (a.decision?.title || a.entry.decision_id)
        .localeCompare(b.decision?.title || b.entry.decision_id, "fr")
    ));

  return (
    <section className="builder-section">
      <div className="builder-section-header">
        <h2 className="builder-section-title" style={{ margin: 0 }}>
          <span className="field-label-row">
            Réservoir de mesures ({entries.length})
            <InfoTooltip text="Les mesures dans lesquelles les pages « probabilistes » de cet atelier piochent. Une mesure prise par les participants ne ressort plus ; une mesure vue mais refusée peut revenir." />
          </span>
        </h2>
        <div className="builder-header-actions">
          <button className="builder-btn-secondary" onClick={() => openDecisionEditor("new")}>
            + Créer une mesure
          </button>
          <button className="builder-btn-secondary" onClick={() => setShowPicker(true)}>
            + Ajouter des mesures
          </button>
        </div>
      </div>

      {entries.length === 0 ? (
        <div className="builder-empty">
          Réservoir vide. Il ne sert qu'aux pages réglées sur « Tirage » dans leur onglet
          Contenu — les pages à liste figée n'en ont pas besoin.
        </div>
      ) : (
        <>
          {/* LE BANDEAU DE DIMENSIONNEMENT, avant la liste : c'est la seule
              question qu'on ne peut pas se poser en regardant les mesures une
              par une. */}
          <div className={slots > entries.length ? "builder-warning-inline" : "builder-hint"}>
            <strong>{entries.length} mesures</strong> pour <strong>{slots} cases</strong>
            {poolPages.length > 0
              ? ` réparties sur ${poolPages.length} page${poolPages.length > 1 ? "s" : ""} qui pioche${poolPages.length > 1 ? "nt" : ""}.`
              : ", mais aucune page de cet atelier ne pioche encore dans le réservoir."}
            {slots > entries.length && (
              <> Si les participants prennent tout ce qu'on leur propose, les derniers tours
              s'afficheront incomplets.</>
            )}
            {unknownCount > 0 && (
              <> ⚠ {unknownCount} mesure(s) du réservoir n'existent plus dans la base.</>
            )}
            {showCosts && (
              <div className="builder-pool-bandcounts">
                {bands.map((band) => (
                  <span key={band.id} className={`builder-chip band-${band.id}`}>
                    {band.label || band.id} · {perBand[band.id] || 0}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* LA BARRE DE LA LISTE : la replier, la filtrer, montrer les coûts.
              Trois commandes sur une ligne, au-dessus de ce qu'elles règlent. */}
          <div className="builder-pool-listbar">
            <button
              type="button"
              className="builder-pool-fold"
              onClick={() => setListOpenChoice(!listOpen)}
              aria-expanded={listOpen}
            >
              {listOpen ? "▾" : "▸"} {entries.length} mesure{entries.length > 1 ? "s" : ""}
              {!listOpen && " — replié"}
            </button>

            {listOpen && entries.length > 8 && (
              <input
                className="builder-search small"
                placeholder="Filtrer la liste…"
                value={listQuery}
                onChange={(e) => setListQuery(e.target.value)}
              />
            )}

            <label className="builder-pool-option">
              <input
                type="checkbox"
                checked={showCosts}
                onChange={(e) => setShowCosts(e.target.checked)}
              />
              <span className="field-label-row">
                Coûts et pondérations
                <InfoTooltip text="Le classement des mesures par coût et leur chance relative de sortir. Ne sert qu'aux pages qui exigent un minimum de mesures par groupe de coût (« au moins 2 peu coûteuses ») ; ailleurs, il n'y a rien à y régler." />
              </span>
            </label>
          </div>

          {showCosts && (
            <div className="builder-pool-costs">
              {/* CE QUE LE COÛT EST, en une phrase : sans elle, les seuils
                  ci-dessous sont trois nombres sans unité. */}
              <p className="builder-hint" style={{ marginTop: 0 }}>
                Le coût d'une mesure est ce qu'elle retire au score
                {" "}<strong>{metricName}</strong>{metricId ? ` (${metricId})` : ""} — le budget
                que dépensent les participants. Les seuils rangent chaque mesure dans un groupe,
                et ces groupes servent de vocabulaire aux planchers des pages qui piochent
                (« au moins 2 peu coûteuses »). Ils ne changent rien au tirage par eux-mêmes.
              </p>

              <div className="builder-pool-bands">
                {bands.map((band, index) => (
                  <label key={band.id} className="builder-pool-band">
                    <span>{band.label || band.id}</span>
                    {index === bands.length - 1 ? (
                      <em>au-delà</em>
                    ) : (
                      <>
                        <span>≤</span>
                        <input
                          type="number"
                          value={band.max_cost ?? ""}
                          onChange={(e) => setBandMax(index, e.target.value)}
                        />
                      </>
                    )}
                  </label>
                ))}
              </div>

              <p className="builder-hint" style={{ margin: 0 }}>
                {costs.length > 0 && (
                  <>Coûts du réservoir : de {costs[0]} à {costs[costs.length - 1]}
                  {" "}(médiane {costs[Math.floor(costs.length / 2)]}). </>
                )}
                {costs.length >= bands.length && (
                  <button className="builder-linkbtn" onClick={suggestBands}>
                    Remplir les seuils pour {bands.length} groupes de taille égale
                  </button>
                )}
              </p>
            </div>
          )}

          {!listOpen && missingRows.length > 0 && (
            <div className="builder-hint">
              Liste repliée — seules les mesures en défaut restent affichées.
            </div>
          )}

          <div className="builder-pool-rows">
            {visibleRows.map(({ entry, index, decision }) => {
              const info = classification[entry.decision_id];
              const chips = decision ? decisionFacetValues(decision).filter(discriminating) : [];
              return (
                <div className="builder-pool-row" key={`${entry.decision_id}-${index}`}>
                  <div className="builder-pool-row-main">
                    {/* LE TITRE EN GRAND, l'identifiant dessous : on reconnaît
                        une mesure à son titre, on la référence par son id. */}
                    <span className="builder-pool-row-title">
                      {decision?.title || entry.decision_id}
                    </span>
                    <span className="builder-pool-row-meta">
                      <code className="builder-pool-row-id">{entry.decision_id}</code>
                      {chips.map(({ key, value, label }) => (
                        <span key={`${key}:${value}`} className="builder-chip subtle" title={value}>
                          {label}
                        </span>
                      ))}
                      {!decision && (
                        <span className="builder-pile-missing">⚠ Mesure introuvable en base</span>
                      )}
                    </span>
                  </div>

                  {showCosts && (
                    <div className="builder-pool-row-costs">
                      {info && (
                        <span className={`builder-chip band-${info.band}`}>
                          {bands.find((b) => b.id === info.band)?.label || info.band}
                          {" · "}{info.cost}
                        </span>
                      )}
                      <label className="builder-pool-weight">
                        poids
                        <input
                          type="number"
                          min="0"
                          step="0.5"
                          placeholder="1"
                          value={entry.weight ?? ""}
                          onChange={(e) => setWeight(index, e.target.value)}
                          title="Chance relative de sortir. Vide = 1, comme les autres."
                        />
                      </label>
                    </div>
                  )}

                  <div className="builder-pool-row-actions">
                    {decision && (
                      <button
                        className="builder-icon-btn"
                        onClick={() => openDecisionEditor(entry.decision_id)}
                        title="Modifier cette mesure — elle est partagée avec les autres ateliers qui l'utilisent"
                      >✏</button>
                    )}
                    <button
                      className="builder-icon-btn danger"
                      onClick={() => removeEntry(index)}
                      title="Retirer du réservoir (la mesure reste dans la base)"
                    >✕</button>
                  </div>
                </div>
              );
            })}
            {listOpen && visibleRows.length === 0 && (
              <div className="builder-empty">
                {trimmedQuery ? "Aucune mesure du réservoir ne correspond." : "Réservoir vide."}
              </div>
            )}
          </div>
        </>
      )}

      {showPicker && (
        <PoolPicker
          allDecisions={allDecisions}
          entries={entries}
          onToggle={toggleDecision}
          onClose={() => setShowPicker(false)}
        />
      )}

      {drawerDecisionId !== null && (
        <SideDrawer
          stack={[{
            label: drawerDecisionId === "new"
              ? "Nouvelle mesure"
              : (decisionsById[drawerDecisionId]?.title || drawerDecisionId),
          }]}
          onClose={closeDecisionDrawer}
          onPop={closeDecisionDrawer}
        >
          <DecisionEditor
            embeddedId={drawerDecisionId}
            onClose={closeDecisionDrawer}
            parentConfig={config}
          />
        </SideDrawer>
      )}
    </section>
  );
}

/**
 * GARNIR LE RÉSERVOIR — deux cents mesures dans la base, trente à retenir.
 *
 * DEUX CHOSES QUE L'ANCIEN SÉLECTEUR NE FAISAIT PAS.
 *
 * 1. IL CHERCHE PAR THÈME autant que par nom : l'étiquette et l'intention
 *    deviennent des filtres cumulables. C'est le geste réel — « les mesures
 *    ACG qui contraignent » — là où une recherche par sous-chaîne obligeait à
 *    connaître déjà le nom de ce qu'on cherche.
 *
 * 2. IL RETIRE AUTANT QU'IL AJOUTE. Une mesure déjà dans le réservoir reste
 *    affichée, cochée : on voit ce qui est pris pendant qu'on choisit, et un
 *    clic de trop se corrige sur place. Elle disparaissait avant de la liste,
 *    ce qui faisait sauter tout ce qui suivait à chaque ajout.
 *
 * IL NE SAIT PAS CRÉER, et c'est voulu : la création vit dans l'en-tête de la
 * section, un cran plus haut. Deux portes vers le même écran, dont l'une passe
 * sous le voile de l'autre, coûtent plus qu'elles ne rendent.
 */
const FACET_LIMIT = 12;

/* LES FACETTES OFFERTES COMME FILTRES, un sous-ensemble de celles qui
   s'affichent sur une ligne. Le secteur et le levier restent lisibles sur
   chaque mesure — ils informent — mais ne servaient pas à chercher : à quatre
   secteurs pour deux cents mesures, le filtre ne restreint presque rien. */
const PICKER_FACETS = ["tags", "category"];

function PoolPicker({ allDecisions, entries, onToggle, onClose }) {
  const [query, setQuery] = useState("");
  const [facets, setFacets] = useState({});

  const inPool = useMemo(() => new Set(entries.map((e) => e.decision_id)), [entries]);
  const index = useMemo(() => buildDecisionIndex(allDecisions), [allDecisions]);
  const groups = useMemo(
    () => facetsOf(allDecisions).filter((group) => PICKER_FACETS.includes(group.key)),
    [allDecisions]
  );

  const toggleFacet = (key, value) => setFacets((current) => {
    const selected = current[key] || [];
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    return { ...current, [key]: next };
  });

  const shown = useMemo(
    () => searchDecisions(index, { query, facets }),
    [index, query, facets]
  );

  const activeFilters = Object.values(facets).reduce((n, v) => n + v.length, 0);
  const clearFilters = () => { setFacets({}); setQuery(""); };

  const LIMIT = 80;

  return (
    <div className="builder-picker-overlay" onClick={onClose}>
      <div className="builder-picker wide" onClick={(e) => e.stopPropagation()}>
        <div className="builder-picker-header">
          <h3>
            Garnir le réservoir
            <span className="builder-chip" style={{ marginLeft: 8 }}>{inPool.size} dedans</span>
          </h3>
          <button onClick={onClose}>✕</button>
        </div>

        <input
          className="builder-search"
          placeholder="Rechercher par titre, identifiant, description, étiquette…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
        />

        <div className="builder-pool-facets">
          {groups.map((group) => (
            <div key={group.key} className="builder-pool-facet">
              <span className="builder-pool-facet-label">{group.label}</span>
              {/* Les valeurs les plus portées d'abord, et pas plus d'une
                  douzaine : au-delà, les pastilles mangeraient la liste
                  qu'elles servent à filtrer, et la recherche textuelle atteint
                  déjà la queue de la distribution. */}
              {group.values.slice(0, FACET_LIMIT).map((value) => (
                <FacetChip
                  key={value.value}
                  label={value.label}
                  count={value.count}
                  title={value.value}
                  on={(facets[group.key] || []).includes(value.value)}
                  onClick={() => toggleFacet(group.key, value.value)}
                />
              ))}
              {group.values.length > FACET_LIMIT && (
                <span className="builder-pool-facet-more">
                  +{group.values.length - FACET_LIMIT} autres — par la recherche
                </span>
              )}
            </div>
          ))}

          {activeFilters > 0 && (
            <button className="builder-linkbtn" onClick={clearFilters}>
              Tout réafficher ({activeFilters} filtre{activeFilters > 1 ? "s" : ""})
            </button>
          )}
        </div>

        <div className="builder-picker-list">
          {shown.slice(0, LIMIT).map((d) => {
            const picked = inPool.has(d._id);
            return (
              <div
                key={d._id}
                className={`builder-picker-item builder-pool-candidate${picked ? " is-picked" : ""}`}
                onClick={() => onToggle(d._id)}
                title={picked ? "Retirer du réservoir" : "Ajouter au réservoir"}
              >
                <span className="builder-pool-candidate-mark">{picked ? "✓" : "+"}</span>
                <span className="builder-pool-candidate-body">
                  <span className="builder-pool-row-title">{d.title || d._id}</span>
                  <span className="builder-pool-row-meta">
                    <code className="builder-pool-row-id">{d._id}</code>
                    {decisionFacetValues(d).map(({ key, value, label }) => (
                      <span key={`${key}:${value}`} className="builder-chip subtle" title={value}>
                        {label}
                      </span>
                    ))}
                  </span>
                </span>
              </div>
            );
          })}
          {shown.length === 0 && <div className="builder-empty">Aucune mesure ne correspond.</div>}
        </div>

        <div className="builder-picker-footer">
          <span className="builder-hint" style={{ margin: 0, marginRight: "auto" }}>
            {shown.length} mesure{shown.length > 1 ? "s" : ""} affichée{shown.length > 1 ? "s" : ""}
            {shown.length > LIMIT && ` (${LIMIT} premières — affinez la recherche)`}
            {" · un clic ajoute ou retire"}
          </span>
          <button className="builder-btn-primary" onClick={onClose}>Terminé</button>
        </div>
      </div>
    </div>
  );
}

function FacetChip({ label, count, title, on, onClick }) {
  return (
    <button
      type="button"
      className={`builder-chip builder-chip-btn${on ? " is-on" : ""}`}
      onClick={onClick}
      title={title}
      aria-pressed={on}
    >
      {label}{count != null && <em className="builder-chip-count">{count}</em>}
    </button>
  );
}
